import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, Check, AlertTriangle, Minus, Plus, Package, FileText, Download, ShoppingCart, Loader2, RefreshCw, Eye, X, Calendar, Info } from 'lucide-react';
import clsx from 'clsx';
import { billingApi, api } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  priceOneShot: number;
  priceYearly: number;
  allowOneShot: boolean;
  allowYearly: boolean;
  usersPerTracker: number;
  geofencesPerTracker: number;
  retentionDays: number;
  features: string[];
  stripePriceIdOneShot?: string;
  stripePriceIdYearly?: string;
  highlightLabel?: string;
  priceDisplay?: 'HT' | 'TTC';
}

interface Subscription {
  id: string;
  status: string;
  interval: 'MONTHLY' | 'YEARLY';
  pricingType?: string;
  quantity: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  startDate?: string;
  expiresAt?: string;
  trialEndsAt?: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
  autoRenewal?: boolean;
  alertsEnabled?: boolean;
  alertDaysBefore?: number;
  notes?: string;
  deviceIds?: string[];
  plan: Plan;
}

interface Invoice {
  id: string;
  invoiceNumber?: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string;
  periodStart?: string;
  periodEnd?: string;
  paidAt?: string;
  invoicePdfUrl?: string;
  hostedInvoiceUrl?: string;
}

interface Usage {
  devices: { used: number; limit: number };
  users: { used: number; limit: number };
  geofences: { used: number; limit: number };
}

type TabId = 'subscriptions' | 'plans' | 'invoices';

// `label` holds an i18n key resolved with t() at render time.
const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'subscriptions', label: 'billing.tab_subscriptions', icon: Package },
  { id: 'plans', label: 'billing.tab_plans', icon: ShoppingCart },
  { id: 'invoices', label: 'billing.tab_invoices', icon: FileText },
];

export default function BillingPage() {
  const { t } = useTranslation();
  const { selectedTenant } = useTenantContextStore();
  const [activeTab, setActiveTab] = useState<TabId>('subscriptions');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [pricingType, setPricingType] = useState<'ONESHOT' | 'YEARLY'>('YEARLY');

  // Subscription info modal
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [devices, setDevices] = useState<{ id: string; name: string; imei: string }[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // PDF viewer state (hoisted to root for proper z-index stacking)
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [selectedTenant?.id]);

  const loadData = async () => {
    try {
      const [plansRes, subsRes, invoicesRes, usageRes] = await Promise.all([
        billingApi.getPlans(),
        billingApi.getSubscriptions().catch(() => ({ data: [] })),
        billingApi.getInvoices().catch(() => ({ data: [] })),
        billingApi.getUsage().catch(() => ({ data: null })),
      ]);
      setPlans(plansRes.data);
      setSubscriptions(Array.isArray(subsRes.data) ? subsRes.data : []);
      setInvoices(invoicesRes.data?.invoices || invoicesRes.data || []);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Erreur chargement données billing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (invoice: Invoice) => {
    setViewingInvoice(invoice);
    setLoadingPdf(true);
    try {
      const res = await api.get(`/billing/invoices/${invoice.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Erreur chargement PDF:', err);
      alert(t('billing.pdf_load_alert'));
      setViewingInvoice(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewingInvoice(null);
  };

  const handleSelectSubscription = async (sub: Subscription) => {
    setSelectedSubscription(sub);
    if (sub.deviceIds && sub.deviceIds.length > 0 && devices.length === 0) {
      setLoadingDevices(true);
      try {
        const res = await api.get('/devices', { params: { limit: 500 } });
        setDevices(res.data?.data || []);
      } catch (err) {
        console.error('Erreur chargement devices:', err);
      } finally {
        setLoadingDevices(false);
      }
    }
  };

  const getQuantity = (planId: string) => quantities[planId] || 1;

  const setQuantity = (planId: string, qty: number) => {
    setQuantities((prev) => ({ ...prev, [planId]: Math.max(1, qty) }));
  };

  const activeSubscriptions = subscriptions.filter((s) => s.status !== 'CANCELED');
  const canceledSubscriptions = subscriptions.filter((s) => s.status === 'CANCELED');
  const totalTrackers = activeSubscriptions.reduce((sum, s) => sum + (s.quantity || 1), 0);

  const handleReactivate = async (sub: Subscription) => {
    setProcessingPlan(sub.id);
    try {
      const res = await billingApi.reactivateSubscription(sub.id);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error: any) {
      console.error('Erreur réactivation:', error);
      alert(error.response?.data?.error || t('billing.reactivation_error'));
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    const priceId = pricingType === 'YEARLY' ? plan.stripePriceIdYearly : plan.stripePriceIdOneShot;
    if (!priceId) {
      alert(t('billing.plan_unavailable'));
      return;
    }

    setProcessingPlan(plan.id);
    try {
      const qty = getQuantity(plan.id);
      const res = await billingApi.createCheckout(priceId, qty);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Erreur checkout:', error);
      alert(t('billing.checkout_error'));
    } finally {
      setProcessingPlan(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-500/20 text-green-600',
      TRIALING: 'bg-blue-500/20 text-blue-600',
      PAST_DUE: 'bg-yellow-500/20 text-yellow-600',
      CANCELED: 'bg-red-500/20 text-red-600',
    };
    const labels: Record<string, string> = {
      ACTIVE: t('billing.status_active'),
      TRIALING: t('billing.status_trialing'),
      PAST_DUE: t('billing.status_past_due'),
      CANCELED: t('billing.status_canceled'),
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getInvoiceStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PAID: 'bg-green-500/20 text-green-600',
      OPEN: 'bg-blue-500/20 text-blue-600',
      DRAFT: 'bg-yellow-500/20 text-yellow-600',
      VOID: 'bg-red-500/20 text-red-600',
      REFUSED: 'bg-red-500/20 text-red-600',
    };
    const labels: Record<string, string> = {
      PAID: t('billing.invoice_paid'),
      OPEN: t('billing.invoice_open'),
      DRAFT: t('billing.invoice_draft'),
      VOID: t('billing.invoice_void'),
      REFUSED: t('billing.invoice_refused'),
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('billing.title')}</h1>
          <p className="text-gray-500 text-sm">{t('billing.subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tab.id === 'subscriptions' ? subscriptions.length
              : tab.id === 'invoices' ? invoices.length
              : null;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'flex items-center gap-2 pb-3 border-b-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <tab.icon className="h-4 w-4" />
                {t(tab.label)}
                {count !== null && count > 0 && (
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                    isActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'subscriptions' && (
        <SubscriptionsTab
          subscriptions={activeSubscriptions}
          canceledSubscriptions={canceledSubscriptions}
          usage={usage}
          totalTrackers={totalTrackers}
          getStatusBadge={getStatusBadge}
          onReactivate={handleReactivate}
          onSelectSubscription={handleSelectSubscription}
          processingPlan={processingPlan}
        />
      )}
      {activeTab === 'plans' && (
        <PlansTab
          plans={plans}
          subscriptions={subscriptions}
          pricingType={pricingType}
          setPricingType={setPricingType}
          getQuantity={getQuantity}
          setQuantity={setQuantity}
          processingPlan={processingPlan}
          onSelectPlan={handleSelectPlan}
        />
      )}
      {activeTab === 'invoices' && (
        <InvoicesTab
          invoices={invoices}
          getInvoiceStatusBadge={getInvoiceStatusBadge}
          onViewPdf={handleViewPdf}
        />
      )}

      {/* PDF Viewer Modal - portal to escape glass-card stacking context */}
      {viewingInvoice && createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl m-4 h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <h2 className="font-semibold text-gray-900">
                    {viewingInvoice.invoiceNumber || t('billing.draft_invoice')}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{viewingInvoice.amountDue.toFixed(2).replace('.', ',')} €</span>
                    {getInvoiceStatusBadge(viewingInvoice.status)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    download={`${viewingInvoice.invoiceNumber || t('billing.invoice_filename_fallback')}.pdf`}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                    title={t('billing.download')}
                  >
                    <Download className="h-5 w-5" />
                  </a>
                )}
                <button onClick={closePdfViewer} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} className="w-full h-full border-0" title={t('billing.invoice_pdf')} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {t('billing.pdf_load_error')}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Subscription Info Modal */}
      {selectedSubscription && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Info className="h-5 w-5 text-primary-600" />
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedSubscription.plan.displayName}</h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    {t('billing.trackers_count', { count: selectedSubscription.quantity })}
                    {getStatusBadge(selectedSubscription.status)}
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedSubscription(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_type')}</label>
                  <p className="text-sm text-gray-900 font-medium">
                    {selectedSubscription.pricingType === 'ONESHOT' ? t('billing.type_oneshot') : selectedSubscription.interval === 'YEARLY' ? t('billing.type_yearly') : t('billing.type_monthly')}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_status')}</label>
                  <div className="mt-0.5">{getStatusBadge(selectedSubscription.status)}</div>
                </div>
                {selectedSubscription.startDate && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_start_date')}</label>
                    <p className="text-sm text-gray-900">{new Date(selectedSubscription.startDate).toLocaleDateString()}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_expires_at')}</label>
                  <p className="text-sm text-gray-900">
                    {(() => {
                      const expDate = selectedSubscription.expiresAt || selectedSubscription.currentPeriodEnd;
                      if (!expDate) return '-';
                      const d = new Date(expDate);
                      const daysLeft = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
                      return (
                        <span className={daysLeft < 0 ? 'text-red-500 font-medium' : daysLeft <= 30 ? 'text-orange-500 font-medium' : ''}>
                          {d.toLocaleDateString()}
                          {daysLeft >= 0 && daysLeft <= 30 && (
                            <span className="text-xs ml-1">{t('billing.days_remaining', { count: Math.ceil(daysLeft) })}</span>
                          )}
                          {daysLeft < 0 && <span className="text-xs ml-1">{t('billing.expired_label')}</span>}
                        </span>
                      );
                    })()}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_auto_renewal')}</label>
                  <p className="text-sm text-gray-900">{selectedSubscription.autoRenewal ? t('billing.yes') : t('billing.no')}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_alerts')}</label>
                  <p className="text-sm text-gray-900">
                    {selectedSubscription.alertsEnabled
                      ? t('billing.alerts_enabled', { count: selectedSubscription.alertDaysBefore || 30 })
                      : t('billing.alerts_disabled')}
                  </p>
                </div>
              </div>
              {selectedSubscription.notes && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_notes')}</label>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 mt-1">{selectedSubscription.notes}</p>
                </div>
              )}
              {selectedSubscription.deviceIds && selectedSubscription.deviceIds.length > 0 && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">{t('billing.detail_linked_trackers', { count: selectedSubscription.deviceIds.length })}</label>
                  {loadingDevices ? (
                    <div className="flex items-center gap-2 mt-1 text-gray-400 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" /> {t('billing.loading')}
                    </div>
                  ) : (
                    <div className="mt-1 space-y-1">
                      {selectedSubscription.deviceIds.map((deviceId) => {
                        const device = devices.find((d) => d.id === deviceId);
                        return (
                          <div key={deviceId} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-1.5">
                            <Package className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-gray-900 font-medium">{device?.name || device?.imei || deviceId.slice(0, 8) + '...'}</span>
                            {device?.imei && device?.name && (
                              <span className="text-gray-400 text-xs">({device.imei})</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ─── Subscriptions Tab ─── */

function SubscriptionsTab({
  subscriptions,
  canceledSubscriptions,
  usage,
  totalTrackers,
  getStatusBadge,
  onReactivate,
  onSelectSubscription,
  processingPlan,
}: {
  subscriptions: Subscription[];
  canceledSubscriptions: Subscription[];
  usage: Usage | null;
  totalTrackers: number;
  getStatusBadge: (status: string) => React.ReactNode;
  onReactivate: (sub: Subscription) => void;
  onSelectSubscription: (sub: Subscription) => void;
  processingPlan: string | null;
}) {
  const { t } = useTranslation();
  if (subscriptions.length === 0 && canceledSubscriptions.length === 0) {
    return (
      <div className="text-center py-16">
        <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-gray-900 font-semibold mb-1">{t('billing.no_subscription_title')}</h3>
        <p className="text-gray-500 text-sm">{t('billing.no_subscription_body')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Usage summary */}
      {usage && subscriptions.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <UsageCard label={t('billing.usage_devices')} used={usage.devices.used} max={usage.devices.limit} />
          <UsageCard label={t('billing.usage_users')} used={usage.users.used} max={usage.users.limit} />
          <UsageCard label={t('billing.usage_geofences')} used={usage.geofences.used} max={usage.geofences.limit} />
        </div>
      )}

      {/* Active subscriptions table */}
      {subscriptions.length > 0 && (
        <div className="card-dark overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200">
            <div className="text-sm text-gray-500">
              {t('billing.trackers_total', { count: totalTrackers })}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_plan')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_trackers')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_type')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_expiration')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_status')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subscriptions.map((sub) => {
                  const endDate = new Date(sub.currentPeriodEnd);
                  const daysLeft = (endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
                  const isExpired = daysLeft < 0;
                  const isExpiringSoon = daysLeft >= 0 && daysLeft <= 30;
                  return (
                  <tr key={sub.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => onSelectSubscription(sub)}>
                    <td className="px-5 py-4">
                      <span className="text-gray-900 font-medium">{sub.plan.displayName}</span>
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      {t('billing.trackers_count', { count: sub.quantity })}
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm">
                      {sub.pricingType === 'ONESHOT' ? t('billing.type_oneshot') : sub.interval === 'YEARLY' ? t('billing.type_yearly') : t('billing.type_monthly')}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <div className={`flex items-center gap-1 ${
                        isExpired ? 'text-red-500 font-medium' : isExpiringSoon ? 'text-orange-500 font-medium' : 'text-gray-500'
                      }`}>
                        <Calendar className="h-3.5 w-3.5" />
                        {endDate.toLocaleDateString()}
                      </div>
                      {isExpiringSoon && !isExpired && (
                        <div className="text-orange-400 text-xs mt-0.5">
                          {t('billing.expires_in_days', { count: Math.ceil(daysLeft) })}
                        </div>
                      )}
                      {sub.trialEndsAt && (
                        <div className="text-blue-500 text-xs mt-0.5">
                          {t('billing.trial_until', { date: new Date(sub.trialEndsAt).toLocaleDateString() })}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {getStatusBadge(sub.status)}
                    </td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {(isExpiringSoon || isExpired) && (sub.plan.stripePriceIdYearly || sub.plan.stripePriceIdOneShot) && (
                          <button
                            onClick={() => onReactivate(sub)}
                            disabled={processingPlan === sub.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                            title={t('billing.pay_via_stripe')}
                          >
                            {processingPlan === sub.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <CreditCard className="h-3.5 w-3.5" />
                            )}
                            {t('billing.renew')}
                          </button>
                        )}
                        {sub.cancelAtPeriodEnd && (
                          <span className="text-yellow-600 text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {t('billing.cancel_scheduled')}
                          </span>
                        )}
                        <button
                          onClick={() => onSelectSubscription(sub)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                          title={t('billing.details')}
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Canceled subscriptions */}
      {canceledSubscriptions.length > 0 && (
        <div className="card-dark overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-700">{t('billing.expired_subs_title')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_plan')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_trackers')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_canceled_at')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_status')}</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {canceledSubscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <span className="text-gray-900 font-medium">{sub.plan.displayName}</span>
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      {t('billing.trackers_count', { count: sub.quantity })}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500">
                      {sub.canceledAt
                        ? new Date(sub.canceledAt).toLocaleDateString()
                        : sub.currentPeriodEnd
                          ? new Date(sub.currentPeriodEnd).toLocaleDateString()
                          : '-'}
                    </td>
                    <td className="px-5 py-4">
                      {getStatusBadge(sub.status)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => onReactivate(sub)}
                        disabled={processingPlan === sub.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {processingPlan === sub.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        {t('billing.reactivate')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Plans Tab ─── */

function PlansTab({
  plans,
  subscriptions,
  pricingType,
  setPricingType,
  getQuantity,
  setQuantity,
  processingPlan,
  onSelectPlan,
}: {
  plans: Plan[];
  subscriptions: Subscription[];
  pricingType: 'ONESHOT' | 'YEARLY';
  setPricingType: (type: 'ONESHOT' | 'YEARLY') => void;
  getQuantity: (planId: string) => number;
  setQuantity: (planId: string, qty: number) => void;
  processingPlan: string | null;
  onSelectPlan: (plan: Plan) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {/* Toggle OneShot / Yearly */}
      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-sm">{t('billing.plans_intro')}</p>
        <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
          <button
            onClick={() => setPricingType('ONESHOT')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              pricingType === 'ONESHOT'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('billing.toggle_oneshot')}
          </button>
          <button
            onClick={() => setPricingType('YEARLY')}
            className={`relative px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              pricingType === 'YEARLY'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('billing.toggle_yearly')}
            {plans.some((p) => p.allowOneShot && p.allowYearly && p.priceYearly < p.priceOneShot) && (
              <span className="ml-2 px-1.5 py-0.5 bg-green-500/20 text-green-600 text-xs rounded-full font-medium">
                {t('billing.savings_badge')}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans
          .filter((plan) => {
            const isFree = plan.priceOneShot === 0 && plan.priceYearly === 0;
            if (isFree) return true;
            return pricingType === 'YEARLY' ? plan.allowYearly : plan.allowOneShot;
          })
          .sort((a, b) => {
            const priceA = pricingType === 'YEARLY' ? a.priceYearly : a.priceOneShot;
            const priceB = pricingType === 'YEARLY' ? b.priceYearly : b.priceOneShot;
            return priceA - priceB;
          })
          .map((plan) => {
            const isFree = plan.priceOneShot === 0 && plan.priceYearly === 0;
            const displayPrice = pricingType === 'YEARLY' ? plan.priceYearly : plan.priceOneShot;
            const monthlyEquivalent = plan.priceYearly / 12;
            const qty = getQuantity(plan.id);
            const existingSub = subscriptions.find((s) => s.plan.id === plan.id);
            const savingsPercent = plan.allowOneShot && plan.allowYearly && plan.priceOneShot > 0
              ? Math.round((1 - plan.priceYearly / plan.priceOneShot) * 100)
              : 0;

            return (
              <div
                key={plan.id}
                className={clsx(
                  'card-dark p-5 relative flex flex-col',
                  plan.highlightLabel && !existingSub && 'ring-2 ring-cyan-500',
                  existingSub && 'ring-2 ring-green-500'
                )}
              >
                {plan.highlightLabel && !existingSub && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-cyan-500 rounded-full text-xs font-medium text-white">
                    {plan.highlightLabel}
                  </div>
                )}
                {existingSub && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-green-500 rounded-full text-xs font-medium text-white whitespace-nowrap">
                    {t('billing.active_count', { count: existingSub.quantity })}
                  </div>
                )}

                <h3 className="text-lg font-bold text-gray-900">{plan.displayName}</h3>
                {plan.description && (
                  <p className="text-gray-500 text-xs mt-0.5">{plan.description}</p>
                )}

                {/* Price */}
                <div className="mt-2 mb-4">
                  {isFree ? (
                    <span className="text-3xl font-bold text-gray-900">{t('billing.free')}</span>
                  ) : pricingType === 'YEARLY' ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-gray-900">{displayPrice}€</span>
                        <span className="text-gray-400 text-sm">{t('billing.per_tracker_year')}</span>
                        <span className="text-gray-400 text-xs">{plan.priceDisplay || 'HT'}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{t('billing.monthly_equivalent', { amount: monthlyEquivalent.toFixed(0) })}</p>
                      {savingsPercent > 0 && (
                        <p className="text-green-500 text-xs font-medium mt-0.5">
                          {t('billing.save_vs_oneshot', { percent: savingsPercent })}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-gray-900">{displayPrice}€</span>
                        <span className="text-gray-400 text-sm">{t('billing.per_tracker')}</span>
                        <span className="text-gray-400 text-xs">{plan.priceDisplay || 'HT'}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{t('billing.oneshot_payment')}</p>
                    </>
                  )}
                </div>

                {/* Quantity selector */}
                {!isFree && (
                  <div className="mb-4">
                    <label className="block text-gray-500 text-xs mb-1">
                      {existingSub ? t('billing.additional_trackers') : t('billing.trackers_quantity')}
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQuantity(plan.id, qty - 1)}
                        className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={qty}
                        onChange={(e) => setQuantity(plan.id, parseInt(e.target.value) || 1)}
                        className="w-14 text-center px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm"
                      />
                      <button
                        onClick={() => setQuantity(plan.id, qty + 1)}
                        className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {qty > 1 && (
                      <p className="text-cyan-500 text-xs mt-1">
                        {t('billing.total_amount', {
                          amount: (displayPrice * qty).toFixed(0),
                          taxLabel: plan.priceDisplay || 'HT',
                          period: pricingType === 'YEARLY' ? t('billing.per_year') : '',
                        })}
                      </p>
                    )}
                  </div>
                )}

                {/* Specs */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('billing.spec_devices')}</span>
                    <span className="text-gray-900 font-medium">{isFree ? '1' : qty}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('billing.spec_users')}</span>
                    <span className="text-gray-900 font-medium">
                      {plan.usersPerTracker === -1 ? t('billing.unlimited') : isFree ? plan.usersPerTracker : qty * plan.usersPerTracker}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('billing.spec_geofences')}</span>
                    <span className="text-gray-900 font-medium">
                      {plan.geofencesPerTracker === -1 ? t('billing.unlimited') : isFree ? plan.geofencesPerTracker : qty * plan.geofencesPerTracker}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('billing.spec_history')}</span>
                    <span className="text-gray-900 font-medium">{t('billing.history_days', { count: plan.retentionDays })}</span>
                  </div>
                </div>

                {/* Features */}
                {plan.features && plan.features.length > 0 && (
                  <div className="border-t border-gray-200 pt-3 mb-4">
                    <ul className="space-y-1.5">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CTA */}
                <div className="mt-auto">
                  {isFree ? (
                    <button disabled className="w-full btn-secondary opacity-50 cursor-not-allowed">
                      {t('billing.free')}
                    </button>
                  ) : (
                    <button
                      onClick={() => onSelectPlan(plan)}
                      disabled={processingPlan === plan.id}
                      className={`w-full ${plan.highlightLabel || existingSub ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      {processingPlan === plan.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          {t('billing.loading')}
                        </span>
                      ) : existingSub ? (
                        t('billing.add_trackers_cta')
                      ) : (
                        t('billing.choose_plan_cta')
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

/* ─── Invoices Tab ─── */

function InvoicesTab({
  invoices,
  getInvoiceStatusBadge,
  onViewPdf,
}: {
  invoices: Invoice[];
  getInvoiceStatusBadge: (status: string) => React.ReactNode;
  onViewPdf: (invoice: Invoice) => void;
}) {
  const { t } = useTranslation();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);

  const handlePay = async (invoice: Invoice) => {
    setPayingId(invoice.id);
    try {
      const res = await billingApi.payInvoice(invoice.id);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Erreur paiement facture:', err);
      alert(t('billing.checkout_error'));
    } finally {
      setPayingId(null);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    setDownloadingId(invoice.id);
    try {
      const res = await api.get(`/billing/invoices/${invoice.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement facture:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  if (invoices.length === 0) {
    return (
      <div className="text-center py-16">
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-gray-900 font-semibold mb-1">{t('billing.no_invoice_title')}</h3>
        <p className="text-gray-500 text-sm">{t('billing.invoices_empty_body')}</p>
      </div>
    );
  }

  return (
    <div className="card-dark overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200">
        <div className="text-sm text-gray-500">
          {t('billing.invoices_count', { count: invoices.length })}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_invoice_number')}</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_date')}</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_expiration')}</th>
              <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_amount')}</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_status')}</th>
              <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.th_actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => onViewPdf(invoice)}>
                <td className="px-5 py-4 text-gray-900 font-medium text-sm">
                  {invoice.invoiceNumber || t('billing.draft_invoice')}
                </td>
                <td className="px-5 py-4 text-gray-500 text-sm">
                  {new Date(invoice.paidAt || invoice.periodStart || '').toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                </td>
                <td className="px-5 py-4 text-sm text-gray-500">
                  {invoice.periodEnd
                    ? new Date(invoice.periodEnd).toLocaleDateString()
                    : '-'}
                </td>
                <td className="px-5 py-4 text-right text-gray-900 font-semibold text-sm">
                  {invoice.amountDue.toFixed(2).replace('.', ',')} €
                </td>
                <td className="px-5 py-4">
                  {getInvoiceStatusBadge(invoice.status)}
                </td>
                <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    {(invoice.status === 'DRAFT' || invoice.status === 'OPEN') && (
                      <button
                        onClick={() => handlePay(invoice)}
                        disabled={payingId === invoice.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
                        title={t('billing.pay_invoice')}
                      >
                        {payingId === invoice.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4" />
                            {t('billing.pay')}
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => onViewPdf(invoice)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
                      title={t('billing.view_pdf')}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(invoice)}
                      disabled={downloadingId === invoice.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                      title={t('billing.download_pdf')}
                    >
                      {downloadingId === invoice.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Usage Card ─── */

function UsageCard({ label, used, max }: { label: string; used: number; max: number }) {
  const { t } = useTranslation();
  const percentage = max === -1 ? 0 : max === 0 ? 0 : Math.min((used / max) * 100, 100);
  const isUnlimited = max === -1;
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && percentage >= 100;

  return (
    <div className="card-dark p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-500 text-sm">{label}</span>
        <span className="text-gray-900 font-semibold">
          {used}<span className="text-gray-400 font-normal">/{isUnlimited ? '∞' : max}</span>
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all',
            isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-cyan-500'
          )}
          style={{ width: isUnlimited ? '0%' : `${percentage}%` }}
        />
      </div>
      {isAtLimit && (
        <p className="text-red-500 text-xs mt-1">{t('billing.limit_reached')}</p>
      )}
    </div>
  );
}
